import { Injectable, Injector } from '@angular/core';
import { WizardModel } from '@configuration/wizard/models/wizard-model';
import { ControlCabinetModel } from '@configuration/wizard/models/types/control-cabinet.model';
import { MCSModel } from '@configuration/wizard/models/types/mcs-model';
import { GDSModel } from '@configuration/wizard/models/types/gds-model';
import { SplicingConnector221Model } from '@configuration/wizard/models/types/splicing-connector221-model';
import { MarkingCardsModel } from '@configuration/wizard/models/types/marking-cards.model';
import { LtlWizardModel } from '@configuration/wizard/models/types/ltl-wizard-model';
import { IoSystemsModel } from '@configuration/wizard/models/types/io-systems.model';
import { IekConfigurationModel } from '@configuration/wizard/models/types/iek-configuration.model';
import { MiniRailModel } from '@configuration/wizard/models/assistants/mini-rail.model';
import { McsMaxiModel } from '@configuration/wizard/models/assistants/mcs-maxi.model';
import { DistributionTerminalsWizardModel } from '@configuration/wizard/models/assistants/distribution-terminals-wizard-model';
import { UserModel } from '@user/model/user.model';

@Injectable()
export class WizardStartupService {
  
  private readonly wizardsByName: Record<string, WizardModel>;

  private static readonly MODEL_CLASSES = [
    WizardStartupService,
    ControlCabinetModel,
    MCSModel,
    GDSModel,
    SplicingConnector221Model,
    MarkingCardsModel,
    LtlWizardModel,
    IoSystemsModel,
    IekConfigurationModel,
    MiniRailModel,
    McsMaxiModel,
    DistributionTerminalsWizardModel,
  ] as const;

  constructor(
    private readonly userModel: UserModel,
    private readonly injector: Injector,
  ) {
    const controlCabinetModel = this.injector.get(ControlCabinetModel);
    const mcsModel = this.injector.get(MCSModel);
    const gdsModel = this.injector.get(GDSModel);
    const splicingConnector221Model = this.injector.get(SplicingConnector221Model);
    const markingCardsModel = this.injector.get(MarkingCardsModel);
    const ltlWizardModel = this.injector.get(LtlWizardModel);
    const ioSystemsModel = this.injector.get(IoSystemsModel);
    const iekConfigurationModel = this.injector.get(IekConfigurationModel);
    const miniRailModel = this.injector.get(MiniRailModel);
    const mcsMaxiModel = this.injector.get(McsMaxiModel);
    const distributionTerminalsWizardModel = this.injector.get(DistributionTerminalsWizardModel);

    this.wizardsByName = {
      'mounting-rail': controlCabinetModel,
      'control-cabinet': controlCabinetModel,
      'mcs': mcsModel,
      'pluggable-connector': mcsModel,
      'gds': gdsModel,
      'pcb-terminal-blocks': gdsModel,
      'splicing-connector-221': splicingConnector221Model,
      '221': splicingConnector221Model,
      'marking-cards': markingCardsModel,
      'ltl': ltlWizardModel,
      'modular-through-connectors': ltlWizardModel,
      'io': ioSystemsModel,
      'io-systems': ioSystemsModel,
      'iek': iekConfigurationModel,
      'distribution-boards': iekConfigurationModel,
      'mini-terminal': miniRailModel,
      'mcs-maxi': mcsMaxiModel,
      'distribution-terminals': distributionTerminalsWizardModel,
    };
  }

  public static provideModels() {
    return WizardStartupService.MODEL_CLASSES;
  }

  public getWizardByNameIfAllowed(wizardName: string): WizardModel | undefined {
    if (!wizardName) {
      return undefined;
    }
    const wizardModel = this.wizardsByName[wizardName.toLowerCase()];
    if(!wizardModel || !this.hasPermission(wizardModel)) {
      return undefined;
    }
    return wizardModel;
  }

  private hasPermission(wizardModel: WizardModel): boolean {
    const neededPermission = wizardModel.config.neededPermission;
    if (!neededPermission) {
      return true;
    }
    return this.userModel.user?.hasPermissions([neededPermission]);
  }
}
